import { useEffect, useState } from "react";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { CgPen, CgTrash } from "react-icons/cg"; // icons for edit/delete
import { Modal } from "react-bootstrap";
import CustomModal from "../Commaon/CustomModal";
import ImageWithLoader from "../Commaon/ImageWithLoader";
import { getWebpFileError } from "../../utils/fileValidation";
import SearchableMultiSelectModal from "../Commaon/SearchableMultiSelectModal";

const getRows = (data) => {
  if (Array.isArray(data?.r)) return data.r;
  if (Array.isArray(data?.r?.data)) return data.r.data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (value.$oid) return value.$oid;
    if (value._id) return normalizeId(value._id);
    if (value.id) return normalizeId(value.id);
    if (typeof value.toString === "function") {
      const stringValue = value.toString();
      return stringValue === "[object Object]" ? "" : stringValue;
    }
  }
  return String(value);
};

const getOptionId = (item) => normalizeId(item?._id || item?.id || item);

const CategoriesList = () => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [addModel, setAddModel] = useState(false);
  const [editModel, setEditModel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [editName, setEditName] = useState("");
  const [editNote, setEditNote] = useState("");
  const [editIsActive, setEditIsActive] = useState(true);
  const [editIcon, setEditIcon] = useState(null);
  const [editIconPreview, setEditIconPreview] = useState("");
  const [editId, setEditId] = useState(null);
  const [deleteModel, setDeleteModel] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [note, setNote] = useState("");
  const [position, setPosition] = useState("");
  const [editPosition, setEditPosition] = useState("");
  const [icon, setIcon] = useState(null);
  const [errors, setErrors] = useState({});
  const [availablePackages, setAvailablePackages] = useState([]);
  const [availableTests, setAvailableTests] = useState([]);
  const [packageIds, setPackageIds] = useState([]);
  const [testIds, setTestIds] = useState([]);
  const [editPackageIds, setEditPackageIds] = useState([]);
  const [editTestIds, setEditTestIds] = useState([]);

  // search + pagination states
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);

  const getCities = async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(`/admin/main-category`, {
        method: "GET",
      });
      const data = response.data;
      if (data.s === 200) {
        setCategories(data.r || []);
        setFilteredCategories(data.r || []);
      }
    } catch (err) {
      console.error("Failed to fetch categories:", err);
      setSnack({
        notification: { message: "Error fetching categories", type: "error" },
      });
      setCategories([]);
      setFilteredCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCities();
  }, []);

  useEffect(() => {
    const loadSelectableItems = async () => {
      try {
        const [packagesResponse, testsResponse] = await Promise.all([
          AuthInterceptor("/admin/package?page=1&limit=500", { method: "GET" }),
          AuthInterceptor("/admin/test?page=1&limit=500", { method: "GET" }),
        ]);
        setAvailablePackages(getRows(packagesResponse.data));
        setAvailableTests(getRows(testsResponse.data));
      } catch (err) {
        setAvailablePackages([]);
        setAvailableTests([]);
      }
    };

    loadSelectableItems();
  }, []);

  const packageOptions = availablePackages
    .map((pkg) => {
      const id = getOptionId(pkg);
      return { value: id, label: pkg.title || pkg.name || id };
    })
    .filter((option) => option.value);

  const testOptions = availableTests
    .map((test) => {
      const id = getOptionId(test);
      return { value: id, label: test.title || test.name || id };
    })
    .filter((option) => option.value);

  // search filter
  useEffect(() => {
    if (!query.trim()) {
      setFilteredCategories(categories);
    } else {
      setFilteredCategories(
        categories.filter((c) =>
          c.name?.toLowerCase().includes(query.toLowerCase())
        )
      );
    }
    setPageNo(1); // reset to first page on search
  }, [query, categories]);

  // pagination slice
  const startIndex = (pageNo - 1) * limit;
  const paginatedCategories = filteredCategories.slice(
    startIndex,
    startIndex + limit
  );

  // action handlers
  const editCategory = (id) => {
    const category = categories.find((c) => c._id === id);
    if (category) {
      setEditId(id);
      setName(category.name);
      setEditName(category.name);
      setEditNote(category.note);
      setEditPosition(category.position ?? "");
      setEditIsActive(category.isActive !== false);
      setEditIcon(null);
      setEditIconPreview(category.icon || "");
      setEditPackageIds((category.packageIds || []).map(normalizeId).filter(Boolean));
      setEditTestIds((category.testIds || []).map(normalizeId).filter(Boolean));
      setEditModel(true);
    }
  };

  const deleteCategory = (id) => {
    setDeleteModel(true);
    setDeleteId(id);
  };

  const addCategory = () => {
    setPackageIds([]);
    setTestIds([]);
    setAddModel(true);
  };

  // ✅ Validation for Add/Edit
  const validateAddForm = (isEdit = false) => {
    let newErrors = {};

    if (!isEdit && !name.trim()) {
      newErrors.name = "Category name is required";
    }
    if (isEdit && !editName.trim()) {
      newErrors.name = "Category name is required";
    }

    if (!isEdit && !note.trim()) {
      newErrors.note = "Note is required";
    }
    if (isEdit && !editNote.trim()) {
      newErrors.note = "Note is required";
    }

    if (!isEdit && !icon) {
      newErrors.icon = "Icon is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!validateAddForm()) return;

    const formData = new FormData();
    formData.append("name", name);
    formData.append("note", note);
    if (position !== "") formData.append("position", position);
    formData.append("isActive", String(isActive));
    formData.append("packageIds", JSON.stringify(packageIds));
    formData.append("testIds", JSON.stringify(testIds));
    const fileError = getWebpFileError(icon);
    if (fileError) {
      setErrors((prev) => ({ ...prev, icon: fileError }));
      return;
    }
    if (icon) formData.append("fileName", icon);

    try {
      setLoading(true);
      console.log(formData);

      const response = await AuthInterceptor(`/admin/main-category`, {
        method: "POST",
        body: formData,
      });
      const data = response.data;

      if (data.s === 201) {
        setCategories((prev) => [...prev, data.r]);
        setFilteredCategories((prev) => [...prev, data.r]);
        setSnack({
          notification: {
            message: "Category added successfully",
            type: "success",
          },
        });
        setAddModel(false);
        setName("");
        setNote("");
        setPosition("");
        setIsActive(true);
        setIcon(null);
        setPackageIds([]);
        setTestIds([]);
        setErrors({});
      } else {
        throw new Error(data.m || "Failed to add Category");
      }
    } catch (err) {
      console.error("Error adding Category:", err);
      setSnack({
        notification: {
          message: err.message || "Error adding Category",
          type: "error",
        },
      });
    } finally {
      getCities();
      setLoading(false);
    }
  };

  const handleEditCategory = async (e) => {
    e.preventDefault();
    if (!validateAddForm(true)) return;

    const formData = new FormData();
    formData.append("name", editName);
    formData.append("note", editNote);
    formData.append("position", editPosition);
    formData.append("isActive", String(editIsActive));
    formData.append("packageIds", JSON.stringify(editPackageIds));
    formData.append("testIds", JSON.stringify(editTestIds));
    const fileError = getWebpFileError(editIcon);
    if (fileError) {
      setErrors((prev) => ({ ...prev, icon: fileError }));
      return;
    }
    if (editIcon) formData.append("fileName", editIcon);

    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/main-category/${editId}`, {
        method: "PUT",
        body: formData,
      });
      const data = response.data;

      if (data.s === 200) {
        setSnack({
          notification: {
            message: "Category updated successfully",
            type: "success",
          },
        });
      } else {
        setSnack({
          notification: {
            message: data.m || "Error updating Category",
            type: "error",
          },
        });
      }
    } catch (err) {
      console.error("Error updating Category:", err);
      setSnack({
        notification: {
          message: err.message || "Error updating Category",
          type: "error",
        },
      });
    } finally {
      getCities();
      setLoading(false);
      setEditModel(false);
      setEditName("");
      setEditId(null);
      setEditIcon(null);
      setEditIconPreview("");
      setEditNote("");
      setEditPosition("");
      setEditIsActive(true);
      setEditPackageIds([]);
      setEditTestIds([]);
      setName("");
      setErrors({});
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/main-category/${id}`, {
        method: "DELETE",
      });
      const data = response.data;

      if (data.s === 200) {
        setSnack({
          notification: {
            message: "Category deleted successfully",
            type: "success",
          },
        });
      }
    } catch (err) {
      console.error("Error deleting Category:", err);
      setSnack({
        notification: {
          message: err.message || "Error deleting Category",
          type: "error",
        },
      });
    } finally {
      getCities();
      setLoading(false);
      setDeleteModel(false);
      setDeleteId(null);
    }
  };

  const toggleCategoryStatus = async (category) => {
    const nextStatus = !category.isActive;
    const formData = new FormData();
    formData.append("isActive", String(nextStatus));
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/main-category/${category._id}`, {
        method: "PUT",
        body: formData,
      });
      const data = response.data;
      if (data.s === 200) {
        setCategories((prev) =>
          prev.map((item) =>
            item._id === category._id ? { ...item, isActive: nextStatus } : item
          )
        );
        setSnack({
          notification: { message: "Category status updated", type: "success" },
        });
      } else {
        throw new Error(data.m || "Error updating category status");
      }
    } catch (err) {
      setSnack({
        notification: { message: err.message || "Error updating category status", type: "error" },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        {/* Header + Page Size */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <h2 className="mb-2 mb-md-0">Categories</h2>
          <div>
            <select
              className="form-select form-select-sm "
              value={limit}
              onChange={(e) => {
                setPageNo(1);
                setLimit(Number(e.target.value));
              }}
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Search */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <input
              className="form-control"
              style={{ width: "40vw" }}
            placeholder="Search categories..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button className="btn btn-outline-custom">Search</button>
          </div>
          <button
            className="btn btn-outline-custom"
            onClick={() => addCategory()}
          >
            Add Category
          </button>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ tableLayout: "fixed", width: "100%" }}>
            <thead className="table-light">
              <tr>
                <th>Id</th>
                <th>Name</th>
                <th>Note</th>
                <th>Position</th>
                <th>Icon</th>
                <th>Packages</th>
                <th>Tests</th>
                <th>Status</th>
                <th style={{ width: "120px" }} className="text-start">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {!loading &&
                paginatedCategories.length > 0 &&
                paginatedCategories.map((c, idx) => (
                  <tr key={c._id || idx}>
                    <td className="fw-semibold">{startIndex + idx + 1}</td>
                    <td>{c.name || "-"}</td>
                    <td>{c.note || "-"}</td>
                    <td>{c.position ?? "-"}</td>
                    <td>
                      {c.icon ? (
                        <ImageWithLoader
                          src={c.icon}
                          alt={c.name}
                          className="rounded"
                          style={{
                            width: "40px",
                            height: "40px",
                            objectFit: "cover",
                            border: "1px solid #ddd",
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{c.packageCount ?? 0}</td>
                    <td>{c.testCount ?? 0}</td>
                    <td>
                      {c.isActive ? (
                        <span className="badge bg-success px-3 py-2" role="button" onClick={() => toggleCategoryStatus(c)}>
                          Active
                        </span>
                      ) : (
                        <span className="badge bg-secondary px-3 py-2" role="button" onClick={() => toggleCategoryStatus(c)}>
                          Inactive
                        </span>
                      )}
                    </td>

                    <td>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => editCategory(c._id)}
                        >
                          <CgPen size={16} />
                        </button>

                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deleteCategory(c._id)}
                        >
                          <CgTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

              {!loading && paginatedCategories.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-4">
                    No categories found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex flex-wrap justify-content-between align-items-center m-3 gap-2">
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() => setPageNo((prev) => Math.max(prev - 1, 1))}
            disabled={pageNo === 1}
          >
            Prev
          </button>
          <span className="mx-2">
            Page {pageNo} of {Math.ceil(filteredCategories.length / limit) || 1}
          </span>
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() =>
              setPageNo((prev) =>
                prev < Math.ceil(filteredCategories.length / limit)
                  ? prev + 1
                  : prev
              )
            }
            disabled={pageNo >= Math.ceil(filteredCategories.length / limit)}
          >
            Next
          </button>
        </div>
      </div>

      {/* Add Category Modal */}
      <Modal show={addModel} onHide={() => setAddModel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleAddCategory}>
            <div className="mb-3">
              <label className="form-label">Category Name</label>
              <input
                type="text"
                className={`form-control ${errors.name ? "is-invalid" : ""}`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
              />
              {errors.name && (
                <div className="invalid-feedback">{errors.name}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Note</label>
              <input
                type="text"
                className={`form-control ${errors.note ? "is-invalid" : ""}`}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={loading}
              />
              {errors.note && (
                <div className="invalid-feedback">{errors.note}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={isActive ? "true" : "false"}
                onChange={(e) => setIsActive(e.target.value === "true")}
                disabled={loading}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Position</label>
              <input
                type="number"
                className="form-control"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Icon</label>
              <input
                type="file"
                className={`form-control ${errors.icon ? "is-invalid" : ""}`}
                accept="image/webp,image/png,image/jpeg"
                onChange={(e) => {
                  const file = e.target.files[0];
                  const fileError = getWebpFileError(file);
                  setIcon(fileError ? null : file);
                  setErrors((prev) => ({ ...prev, icon: fileError }));
                }}
                disabled={loading}
              />
              {errors.icon && (
                <div className="invalid-feedback">{errors.icon}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Packages</label>
              <SearchableMultiSelectModal
                title="Select Packages"
                options={packageOptions}
                selectedValues={packageIds}
                onChange={setPackageIds}
                buttonLabel="Select packages"
                emptyLabel="No packages selected"
                disabled={loading}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Tests</label>
              <SearchableMultiSelectModal
                title="Select Tests"
                options={testOptions}
                selectedValues={testIds}
                onChange={setTestIds}
                buttonLabel="Select tests"
                emptyLabel="No tests selected"
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <Spinnerr show={loading} /> : "Add Category"}
            </button>
          </form>
        </Modal.Body>
      </Modal>

      {/* Edit Category Modal */}
      <Modal show={editModel} onHide={() => setEditModel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleEditCategory}>
            <div className="mb-3">
              <div className="mb-3">Current Name: {name}</div>
              <label className="form-label">Category Name</label>
              <input
                type="text"
                className={`form-control ${errors.name ? "is-invalid" : ""}`}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                disabled={loading}
              />
              {errors.name && (
                <div className="invalid-feedback">{errors.name}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Note</label>
              <input
                type="text"
                className={`form-control ${errors.note ? "is-invalid" : ""}`}
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                disabled={loading}
              />
              {errors.note && (
                <div className="invalid-feedback">{errors.note}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={editIsActive ? "true" : "false"}
                onChange={(e) => setEditIsActive(e.target.value === "true")}
                disabled={loading}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Position</label>
              <input
                type="number"
                className="form-control"
                value={editPosition}
                onChange={(e) => setEditPosition(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Icon</label>
              <input
                type="file"
                className="form-control"
                accept="image/webp,image/png,image/jpeg"
                onChange={(e) => {
                  const file = e.target.files[0];
                  const fileError = getWebpFileError(file);
                  setEditIcon(fileError ? null : file);
                  setErrors((prev) => ({ ...prev, icon: fileError }));
                }}
                disabled={loading}
              />
              {errors.icon && (
                <div className="invalid-feedback d-block">{errors.icon}</div>
              )}
              {editIconPreview && !editIcon && (
                <div className="mt-2">
                  <div className="text-muted small mb-1">Current Icon</div>
                  <ImageWithLoader
                    src={editIconPreview}
                    alt={editName}
                    style={{ width: "64px", height: "64px", objectFit: "cover" }}
                  />
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Packages</label>
              <SearchableMultiSelectModal
                title="Select Packages"
                options={packageOptions}
                selectedValues={editPackageIds}
                onChange={setEditPackageIds}
                buttonLabel="Select packages"
                emptyLabel="No packages selected"
                disabled={loading}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Tests</label>
              <SearchableMultiSelectModal
                title="Select Tests"
                options={testOptions}
                selectedValues={editTestIds}
                onChange={setEditTestIds}
                buttonLabel="Select tests"
                emptyLabel="No tests selected"
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <Spinnerr show={loading} /> : "Update Category"}
            </button>
          </form>
        </Modal.Body>
      </Modal>

      {/* Delete Category Modal */}
      <CustomModal
        show={deleteModel}
        title="Confirm Delete"
        message="Are you sure you want to delete this Category?"
        onConfirm={() => {
          handleDeleteCategory(deleteId);
          setDeleteModel(false);
        }}
        onCancel={() => setDeleteModel(false)}
      />
    </>
  );
};

export default CategoriesList;
